import { useState } from 'react'
import { Power } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Button } from '@/components/ui/button'
import { TopUpCreditsModal } from '@/components/settings/top-up-credits-modal'
import { useAppSettings, useUpdateAppSettings } from '@/hooks/use-settings'
import { useCreditBalances } from '@/hooks/use-credits'
import { cn } from '@/lib/utils'
import type { NotificationChannel } from '@riverpark/types'

function ToggleRow({
  title,
  description,
  enabled,
  disabled,
  onToggle,
}: {
  title: string
  description: string
  enabled: boolean
  disabled: boolean
  onToggle: () => void
}) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div>
        <p className="font-sans text-sm font-medium text-ink">{title}</p>
        <p className="mt-1 font-sans text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        type="button"
        onClick={onToggle}
        disabled={disabled}
        title={enabled ? 'Turn off' : 'Turn on'}
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 rounded-full px-3.5 py-1.5 font-sans text-xs font-semibold uppercase tracking-wide transition-opacity disabled:cursor-not-allowed disabled:opacity-50',
          enabled
            ? 'bg-status-paid-bg text-status-paid-text hover:opacity-80'
            : 'bg-status-draft-bg text-status-draft-text hover:opacity-80',
        )}
      >
        <Power size={14} strokeWidth={2} />
        {enabled ? 'On' : 'Off'}
      </button>
    </div>
  )
}

function CreditRow({
  label,
  description,
  balance,
  isLoading,
  onTopUp,
}: {
  label: string
  description: string
  balance: number | undefined
  isLoading: boolean
  onTopUp: () => void
}) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div>
        <p className="font-sans text-sm font-medium text-ink">{label}</p>
        <p className="mt-1 font-sans text-xs text-muted-foreground">{description}</p>
      </div>
      <div className="flex items-center gap-3">
        <p className="font-sans text-sm tabular-nums text-ink">
          {isLoading ? '…' : (balance ?? 0).toLocaleString('en-KE')}
          <span className="ml-1 text-xs text-muted-foreground">credits</span>
        </p>
        <Button type="button" size="sm" variant="secondary" onClick={onTopUp}>
          Top up
        </Button>
      </div>
    </div>
  )
}

export function AdminSettings() {
  const { data, isLoading } = useAppSettings()
  const update = useUpdateAppSettings()
  const smsEnabled = data?.data.smsEnabled ?? true
  const billingEmailEnabled = data?.data.billingEmailEnabled ?? true

  const { data: creditsData, isLoading: creditsLoading } = useCreditBalances()
  const [topUpChannel, setTopUpChannel] = useState<NotificationChannel | null>(null)

  async function handleToggleSms() {
    if (smsEnabled) {
      const confirmed = confirm(
        'Turn off SMS notifications? Every invoice, payment, and overdue-reminder message will be silently skipped (not delivered) until this is switched back on.',
      )
      if (!confirmed) return
    }
    await update.mutateAsync({ smsEnabled: !smsEnabled })
  }

  async function handleToggleBillingEmail() {
    if (billingEmailEnabled) {
      const confirmed = confirm(
        'Turn off invoice-summary emails? The aggregate statement (with PDF and pay-now link) will be silently skipped until this is switched back on.',
      )
      if (!confirmed) return
    }
    await update.mutateAsync({ billingEmailEnabled: !billingEmailEnabled })
  }

  return (
    <PageShell>
      <PageHeader label="System" title="Settings" />

      <div className="max-w-xl divide-y divide-border-2 rounded border border-border-2 bg-card">
        <div className="border-b border-border-2 px-5 py-3">
          <h2 className="font-serif text-lg text-ink">Notifications</h2>
        </div>
        <ToggleRow
          title="SMS notifications"
          description="Invoice, payment, and overdue-reminder messages. Turning this off silently skips every send — nothing is delivered, but it's still logged."
          enabled={smsEnabled}
          disabled={isLoading || update.isPending}
          onToggle={() => void handleToggleSms()}
        />
        <ToggleRow
          title="Invoice-summary emails"
          description="Aggregate outstanding-balance email with a PDF statement and a one-click pay-now link, sent whenever an invoice is marked sent."
          enabled={billingEmailEnabled}
          disabled={isLoading || update.isPending}
          onToggle={() => void handleToggleBillingEmail()}
        />
      </div>

      <div className="mt-6 max-w-xl divide-y divide-border-2 rounded border border-border-2 bg-card">
        <div className="border-b border-border-2 px-5 py-3">
          <h2 className="font-serif text-lg text-ink">Credits</h2>
          <p className="mt-1 font-sans text-xs text-muted-foreground">
            A bulk send (announcement broadcast, invoice batch) checks this balance before it starts
            and refuses to send at all if there aren't enough credits for every recipient — it never
            fails partway through. Single messages (payment confirmations, OTPs, invites) aren't
            affected by this balance.
          </p>
        </div>
        <CreditRow
          label="SMS"
          description="Africa's Talking — top up after buying real airtime."
          balance={creditsData?.data.sms}
          isLoading={creditsLoading}
          onTopUp={() => setTopUpChannel('sms')}
        />
        <CreditRow
          label="Email"
          description="ZeptoMail — top up after buying real email credits."
          balance={creditsData?.data.email}
          isLoading={creditsLoading}
          onTopUp={() => setTopUpChannel('email')}
        />
      </div>

      {topUpChannel && (
        <TopUpCreditsModal channel={topUpChannel} onClose={() => setTopUpChannel(null)} />
      )}
    </PageShell>
  )
}
